import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Lenis from "lenis";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { motion, useScroll, useTransform } from "framer-motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  Wrench, Bell, QrCode, ShieldCheck, Users, LineChart,
  ArrowUpRight, Check, Apple, Play, Gauge, Truck, Tractor, Star, Send, CheckCircle2,
  MapPin, Download, WifiOff, ClipboardCheck, Facebook,
} from "lucide-react";

const IMG = {
  hero: "https://images.unsplash.com/photo-1761504537447-e39a3085d697?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwzfHxleGNhdmF0b3IlMjBjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwbmlnaHR8ZW58MHx8fHwxNzg2NTEzNzE4fDA&ixlib=rb-4.1.0&q=85",
  cat: "https://images.unsplash.com/photo-1650127664508-30f4321d9192?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHw0fHxleGNhdmF0b3IlMjBjb25zdHJ1Y3Rpb24lMjBtYWNoaW5lcnklMjBkYXJrfGVufDB8fHx8MTc4NTE3NjIyNnww&ixlib=rb-4.1.0&q=85",
  tractor: "https://images.pexels.com/photos/30425493/pexels-photo-30425493.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  truck: "https://images.unsplash.com/photo-1520857622823-2df34928e183",
  bulldozer: "https://images.unsplash.com/photo-1728069185264-ed82a210f385?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxleGNhdmF0b3IlMjBjb25zdHJ1Y3Rpb24lMjBtYWNoaW5lcnklMjBkYXJrfGVufDB8fHx8MTc4NTE3NjIyNnww&ixlib=rb-4.1.0&q=85",
  farmer: "https://images.unsplash.com/photo-1724996854069-a7d335193ee2",
};

const HERO_LINES = ["KEEP EVERY", "MACHINE", "RUNNING."];

function Reveal({ children, delay = 0, y = 40 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

function Nav() {
  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-[#0D0E11]/95 border-b border-white/10" data-testid="landing-nav">
      <div className="max-w-[1400px] mx-auto px-6 h-[68px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
          <div className="w-8 h-8 bg-[#FFB800] flex items-center justify-center">
            <Wrench className="w-5 h-5 text-black" strokeWidth={2.5} />
          </div>
          <span className="font-head font-900 text-2xl tracking-tight">IRONLOG</span>
        </Link>
        <nav className="hidden md:flex items-center gap-8 font-body text-sm text-[#94A3B8]">
          <a href="#features" className="hover:text-white transition-colors">Features</a>
          <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
          <a href="#field" className="hover:text-white transition-colors">In The Field</a>
          <a href="#app" className="hover:text-white transition-colors">Mobile App</a>
          <a href="#demo" className="hover:text-white transition-colors" data-testid="nav-demo-link">Book a Demo</a>
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/login" className="font-head font-700 text-sm uppercase tracking-wide px-4 py-2 text-white hover:text-[#FFB800] transition-colors" data-testid="nav-login-link">Log In</Link>
          <Link to="/register" className="btn-signal font-head text-sm h-10 px-5 flex items-center" data-testid="nav-signup-link">Start Free</Link>
        </div>
      </div>
    </header>
  );
}

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section ref={ref} className="relative min-h-screen pt-[68px] overflow-hidden blueprint" data-testid="hero-section">
      <motion.div className="absolute inset-0 z-0" style={{ y: imgY, scale: imgScale }}>
        <img src={IMG.hero} alt="Heavy machinery at night" className="w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(13,14,17,0.4), rgba(13,14,17,0.85) 60%, #0D0E11)" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to right, #0D0E11 5%, transparent 55%)" }} />
      </motion.div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 pt-16 md:pt-24 pb-20">
        <motion.div
          initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 border border-[#FFB800]/40 bg-[#FFB800]/10 px-3 py-1.5 mb-8"
        >
          <span className="w-2 h-2 bg-[#00E676] animate-pulse" />
          <span className="font-mono text-xs text-[#FFB800] tracking-wider uppercase">Fleet Maintenance Command Center</span>
        </motion.div>

        <h1 className="font-head font-900 leading-[0.86] tracking-tighter text-[16vw] md:text-[10vw] lg:text-[9.5rem]">
          {HERO_LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                className="block"
                initial={{ y: "110%" }} animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
              >
                {i === 2 ? <span className="text-[#FFB800]">{line}</span> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7, duration: 0.7 }}
          className="mt-10 grid md:grid-cols-[1fr_auto] gap-8 items-end"
        >
          <p className="font-body text-lg md:text-xl text-[#94A3B8] max-w-xl leading-relaxed">
            Track services, reminders and running hours across your whole fleet. Built rugged for farmers, councils, landscapers and contractors — not another soft SaaS dashboard.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/register" className="btn-signal font-head h-14 px-8 text-lg flex items-center justify-center gap-2" data-testid="hero-cta-primary">
              Start Free <ArrowUpRight className="w-5 h-5" strokeWidth={2.5} />
            </Link>
            <a href="#pricing" className="btn-ghost font-head h-14 px-8 text-lg flex items-center justify-center" data-testid="hero-cta-pricing">See Pricing</a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Marquee() {
  const items = ["SERVICE TRACKING", "REGO REMINDERS", "QR MACHINE TAGS", "RUNNING HOURS", "TEAM ACCOUNTABILITY", "SERVICE HISTORY", "OVERDUE ALERTS"];
  const row = [...items, ...items];
  return (
    <div className="border-y border-white/10 bg-[#FFB800] py-4 overflow-hidden" data-testid="marquee">
      <div className="marquee">
        <div className="marquee__track">
          {row.map((t, i) => (
            <span key={i} className="font-head font-800 text-3xl md:text-4xl text-black tracking-tight uppercase px-8 flex items-center gap-8">
              {t} <span className="text-black/40">✦</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

const CHAPTERS = [
  { n: "01", t: "One fleet, total oversight", d: "Managers see every machine, every log, every worker. Workers add machines and log services from the field — you keep the full picture." },
  { n: "02", t: "Nothing slips through", d: "Service intervals, blades, belts, greasing, hydraulic oil, rego, insurance, warranty. Recurring by days or by machine hours — with overdue and upcoming status." },
  { n: "03", t: "Rego, handled automatically", d: "Set a registration renewal date and IronLog auto-creates reminders at 45, 30 and 15 days out. No spreadsheet, no missed deadline." },
];

function Manifesto() {
  return (
    <section className="max-w-[1400px] mx-auto px-6 py-28" data-testid="manifesto">
      <Reveal>
        <div className="flex items-center gap-3 mb-14">
          <span className="w-8 h-[2px] bg-[#FFB800]" />
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#FFB800]">Why IronLog</span>
        </div>
      </Reveal>
      <div className="space-y-2">
        {CHAPTERS.map((c, i) => (
          <Reveal key={c.n} delay={i * 0.08}>
            <div className="grid md:grid-cols-[140px_1fr] gap-6 md:gap-12 py-10 border-t border-white/10 group">
              <span className="font-mono text-[#FFB800]/70 text-xl">{c.n}</span>
              <div className="grid md:grid-cols-2 gap-6 items-start">
                <h3 className="font-head font-800 text-4xl md:text-5xl leading-[0.95] tracking-tight uppercase group-hover:text-[#FFB800] transition-colors duration-300">{c.t}</h3>
                <p className="font-body text-[#94A3B8] text-lg leading-relaxed">{c.d}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

const FEATURES = [
  { icon: Gauge, t: "Command Dashboard", d: "KPI cards, fleet-wide machine list and a live activity feed of who did what, when." },
  { icon: Bell, t: "Smart Reminders", d: "Due dates + recurring intervals by days or hours. Overdue and upcoming, front and centre." },
  { icon: Wrench, t: "Service History", d: "Log parts, cost, hours and photos. Every service builds a timeline per machine." },
  { icon: QrCode, t: "QR Machine Tags", d: "Printable QR per machine. Scan on the tractor to pull its full history instantly." },
  { icon: Users, t: "Team & Roles", d: "Manager and Worker roles in one shared fleet. Invite workers with a code." },
  { icon: ShieldCheck, t: "Accountability", d: "Every machine and log records who added or logged it. Full oversight, always." },
];

function Features() {
  return (
    <section id="features" className="bg-[#0D0E11] border-t border-white/10 py-28" data-testid="features-section">
      <div className="max-w-[1400px] mx-auto px-6">
        <Reveal>
          <h2 className="font-head font-900 text-5xl md:text-7xl tracking-tighter uppercase max-w-3xl leading-[0.9]">
            Everything you need to <span className="text-[#FFB800]">stay ahead of failure</span>
          </h2>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-16">
          {FEATURES.map((f, i) => (
            <Reveal key={f.t} delay={(i % 3) * 0.08}>
              <div className="card p-8 h-full hover:border-[#FFB800]/40 transition-colors duration-300 group" data-testid={`feature-card-${i}`}>
                <div className="w-12 h-12 bg-[#0D0E11] border border-white/10 flex items-center justify-center mb-6 group-hover:border-[#FFB800] transition-colors">
                  <f.icon className="w-6 h-6 text-[#FFB800]" strokeWidth={2} />
                </div>
                <h3 className="font-head font-700 text-2xl uppercase tracking-tight mb-2">{f.t}</h3>
                <p className="font-body text-[#94A3B8] leading-relaxed">{f.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function FieldStrip() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], ["-8%", "12%"]);
  return (
    <section id="field" ref={ref} className="relative py-28 overflow-hidden" data-testid="field-section">
      <div className="max-w-[1400px] mx-auto px-6 grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 relative overflow-hidden clip-slant h-[420px]">
          <motion.img style={{ y: y1 }} src={IMG.cat} alt="CAT excavator" className="w-full h-[130%] object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to top, #0D0E11, transparent 60%)" }} />
          <div className="absolute bottom-8 left-8">
            <span className="font-mono text-xs text-[#FFB800] uppercase tracking-widest">Built for the yard</span>
            <h3 className="font-head font-900 text-4xl md:text-5xl uppercase tracking-tight mt-2 max-w-md">Glove-friendly. Field-ready.</h3>
          </div>
        </div>
        <div className="grid grid-rows-2 gap-4">
          <div className="relative overflow-hidden h-[200px]">
            <img src={IMG.tractor} alt="Tractor" className="w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to top, #0D0E11, transparent 70%)" }} />
          </div>
          <div className="card p-6 flex flex-col justify-center">
            <p className="font-mono text-5xl text-[#FFB800]">45/30/15</p>
            <p className="font-body text-[#94A3B8] mt-2">Auto rego reminders, days before expiry — created the moment you set a renewal date.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

const TIERS = [
  { name: "Free Demo", price: 0, unit: "", machines: "2 machines", plan: "free", features: ["2 machines", "Up to 4 team members", "Service logging & history", "Basic — no reminders, QR or photos"] },
  { name: "Individual", price: 10, unit: "/mo", machines: "5–15 machines", plan: "ind_5", features: ["A$10 → 5 machines", "A$20 → 10 machines", "A$30 → 15 machines", "Shared across your team"], highlight: true },
  { name: "Business", price: 50, unit: "/mo", machines: "20 – Unlimited", plan: "biz_20", features: ["A$50 → 20 machines", "A$100 → 50 machines", "A$200 → Unlimited", "Priority for big fleets"] },
];

const COMPARE_ROWS = [
  { label: "Machines included", free: "2", ind: "5 – 15", biz: "20 – ∞", head: true },
  { label: "Price per month (AUD)", free: "A$0", ind: "A$10–30", biz: "A$50–200", head: true },
  { label: "Team members", free: "Up to 4", ind: "Unlimited", biz: "Unlimited" },
  { label: "Manager & Worker roles", free: true, ind: true, biz: true },
  { label: "Add & edit machines", free: true, ind: true, biz: true },
  { label: "Service logging & full history", free: true, ind: true, biz: true },
  { label: "Activity feed & accountability", free: true, ind: true, biz: true },
  { label: "Search, filter & sort by overdue", free: true, ind: true, biz: true },
  { label: "PDF history export", free: true, ind: true, biz: true },
  { label: "Machine photos", free: false, ind: true, biz: true },
  { label: "QR machine tags", free: false, ind: true, biz: true },
  { label: "Smart reminders (days & hours)", free: false, ind: true, biz: true },
  { label: "Auto rego reminders (45/30/15)", free: false, ind: true, biz: true },
  { label: "Blades, belts, greasing & hydraulics", free: false, ind: true, biz: true },
  { label: "Insurance, warranty & parts tracking", free: false, ind: true, biz: true },
  { label: "Mobile app sync", free: true, ind: true, biz: true },
  { label: "Support", free: "Community", ind: "Email", biz: "Priority" },
  { label: "Guided onboarding & data import", free: false, ind: false, biz: true },
];

function Cell({ v }) {
  if (v === true) return <Check className="w-5 h-5 text-[#00E676] mx-auto" strokeWidth={3} />;
  if (v === false) return <span className="text-white/20">—</span>;
  return <span className="font-mono text-sm text-white">{v}</span>;
}

function ComparisonTable() {
  const cols = [
    { key: "free", name: "Free Demo" },
    { key: "ind", name: "Individual" },
    { key: "biz", name: "Business" },
  ];
  return (
    <Reveal>
      <div className="mt-6" data-testid="pricing-comparison">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-8 h-[2px] bg-[#FFB800]" />
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#FFB800]">Compare Every Plan</span>
        </div>
        <div className="overflow-x-auto border border-white/10">
          <table className="w-full min-w-[720px] border-collapse" data-testid="comparison-table">
            <thead>
              <tr className="bg-[#0D0E11]">
                <th className="text-left font-head font-700 uppercase tracking-wide text-[#94A3B8] text-sm p-4 w-[38%]">What you get</th>
                {cols.map((c) => (
                  <th key={c.key} className={`p-4 text-center font-head font-800 uppercase tracking-tight text-xl border-l border-white/10 ${c.key === "ind" ? "text-[#FFB800] bg-[#FFB800]/5" : "text-white"}`}>{c.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARE_ROWS.map((r, i) => (
                <tr key={r.label} className={`border-t border-white/10 ${r.head ? "bg-[#1A1C23]" : i % 2 ? "bg-[#15171d]" : "bg-[#1A1C23]"}`} data-testid={`compare-row-${i}`}>
                  <td className={`p-4 font-body ${r.head ? "text-white font-700" : "text-[#94A3B8]"} text-sm`}>{r.label}</td>
                  {cols.map((c) => (
                    <td key={c.key} className={`p-4 text-center border-l border-white/10 ${c.key === "ind" ? "bg-[#FFB800]/5" : ""} ${r.head ? "font-mono text-[#FFB800]" : ""}`}>
                      {r.head ? <span className="font-mono text-base">{r[c.key]}</span> : <Cell v={r[c.key]} />}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="font-body text-xs text-[#94A3B8] mt-3">Free Demo covers the basics — 2 machines and up to 4 team members. Paid plans unlock reminders, QR tags and photos. Individual scales A$10/A$20/A$30 (5/10/15 machines); Business scales A$50/A$100/A$200 (20/50/Unlimited).</p>
      </div>
    </Reveal>
  );
}

const FAQS = [
  { q: "Are prices in AUD and do they include GST?", a: "Yes — every price is in Australian dollars. Prices shown are ex-GST; any applicable GST is calculated at checkout." },
  { q: "Does one plan cover my whole team?", a: "Yes. The manager's plan covers unlimited workers in the fleet — you only pay for machine capacity, never per seat." },
  { q: "What happens if I hit my machine limit?", a: "You can keep using every existing machine. To add more, just upgrade to the next tier — it takes effect immediately." },
  { q: "Can I cancel anytime?", a: "Absolutely. Cancel from the Billing screen and you'll drop back to the Free Demo. Machines over the free cap stay visible; you just can't add new ones until you upgrade again." },
  { q: "How do I switch plans?", a: "Upgrade or change tier anytime from Billing. Your machine limit updates the moment payment is confirmed." },
  { q: "Which payment methods do you accept?", a: "All major cards via secure Stripe Checkout. Your card details never touch our servers." },
  { q: "Do you offer discount codes?", a: "Yes — if you've been given a promo code, enter it on the Billing screen before you upgrade and the discount is applied at checkout." },
];

function PricingFAQ() {
  return (
    <Reveal>
      <div className="mt-16" data-testid="pricing-faq">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-8 h-[2px] bg-[#FFB800]" />
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#FFB800]">Pricing FAQ</span>
        </div>
        <Accordion type="single" collapsible className="border border-white/10">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border-b border-white/10 last:border-0 px-5" data-testid={`faq-${i}`}>
              <AccordionTrigger className="font-head font-700 text-lg md:text-xl uppercase tracking-tight text-left hover:text-[#FFB800] hover:no-underline py-5">{f.q}</AccordionTrigger>
              <AccordionContent className="font-body text-[#94A3B8] text-base pb-5">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </Reveal>
  );
}

function Pricing() {
  return (
    <section id="pricing" className="bg-[#0D0E11] border-t border-white/10 py-28" data-testid="pricing-section">
      <div className="max-w-[1400px] mx-auto px-6">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <h2 className="font-head font-900 text-5xl md:text-7xl tracking-tighter uppercase leading-[0.9]">One plan.<br/>Whole <span className="text-[#FFB800]">fleet.</span></h2>
            <p className="font-body text-[#94A3B8] max-w-sm text-lg">Priced in Australian dollars (AUD). The manager&apos;s plan covers every worker — you only pay for machine capacity.</p>
          </div>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-4">
          {TIERS.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.08}>
              <div className={`p-8 h-full flex flex-col border ${t.highlight ? "bg-[#1A1C23] border-[#FFB800]" : "card"}`} data-testid={`pricing-tier-${t.plan}`}>
                {t.highlight && <span className="badge badge-upcoming self-start mb-4">Most Popular</span>}
                <h3 className="font-head font-800 text-3xl uppercase tracking-tight">{t.name}</h3>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="font-mono text-5xl text-white">A${t.price}</span>
                  <span className="font-mono text-[#94A3B8]">{t.unit ? `${t.unit} AUD` : " AUD"}</span>
                </div>
                <p className="font-mono text-sm text-[#FFB800] mt-2">{t.machines}</p>
                <ul className="mt-6 space-y-3 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 font-body text-[#94A3B8]">
                      <Check className="w-4 h-4 text-[#00E676] mt-1 shrink-0" strokeWidth={3} /> {f}
                    </li>
                  ))}
                </ul>
                <Link to="/register" className={`mt-8 h-14 flex items-center justify-center font-head text-lg ${t.highlight ? "btn-signal" : "btn-ghost"}`} data-testid={`pricing-cta-${t.plan}`}>
                  Get Started
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
        <ComparisonTable />
        <PricingFAQ />
      </div>
    </section>
  );
}

// Real customer reviews go here as you collect them. Shape:
// { q: "quote", n: "Name", r: "Role, Business", img: "https://…" (optional) }
const TESTIMONIALS = [];

const TRUST_SIGNALS = [
  { icon: MapPin, t: "Built for Aussie operators", d: "Rego renewals, compliance and AUD pricing — made for local farms, councils, landscapers and contractors." },
  { icon: ShieldCheck, t: "Bank-grade secure checkout", d: "Payments run through Stripe. We never see or store your card details." },
  { icon: Download, t: "Your data, always yours", d: "Export a machine's full history and compliance records to PDF & CSV anytime." },
  { icon: ClipboardCheck, t: "Audit-ready compliance", d: "One-tap pre-start and compliance PDF reports, built for insurers and auditors." },
  { icon: WifiOff, t: "Works offline in the yard", d: "Log pre-starts with no signal — they sync automatically once you're back online." },
  { icon: QrCode, t: "QR tag every machine", d: "Scan a sticker to pull up a machine's history and log a pre-start on the spot." },
];

function ReviewCard({ t, i }) {
  const initials = (t.n || "").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  return (
    <div className="card p-6 h-full flex flex-col justify-between break-inside-avoid mb-4" data-testid={`testimonial-${i}`}>
      <div>
        <div className="flex gap-0.5 mb-3">
          {Array.from({ length: 5 }).map((_, s) => (
            <Star key={s} className="w-3.5 h-3.5 text-[#FFB800] fill-[#FFB800]" />
          ))}
        </div>
        <p className="font-body text-white/90 text-lg leading-snug">&ldquo;{t.q}&rdquo;</p>
      </div>
      <div className="flex items-center gap-3 mt-6 pt-4 border-t border-white/10">
        {t.img
          ? <img src={t.img} alt={t.n} loading="lazy" className="w-11 h-11 object-cover grayscale shrink-0" />
          : <span className="w-11 h-11 shrink-0 bg-[#FFB800]/15 text-[#FFB800] font-head font-800 flex items-center justify-center">{initials}</span>}
        <div className="min-w-0">
          <p className="font-head font-700 text-white uppercase tracking-tight truncate">{t.n}</p>
          <p className="font-mono text-xs text-[#94A3B8] truncate">{t.r}</p>
        </div>
      </div>
    </div>
  );
}

function Testimonials() {
  const hasReviews = TESTIMONIALS.length > 0;
  return (
    <section id="reviews" className="max-w-[1400px] mx-auto px-6 py-28" data-testid="testimonials">
      <Reveal>
        <div className="flex items-center gap-3 mb-4">
          <span className="w-8 h-[2px] bg-[#FFB800]" />
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#FFB800]">Why Crews Choose IronLog</span>
        </div>
        <h2 className="font-head font-900 text-5xl md:text-7xl uppercase tracking-tighter leading-[0.9] mb-14">Built for the yard,<br/>not the <span className="text-[#FFB800]">boardroom.</span></h2>
      </Reveal>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="trust-signals">
        {TRUST_SIGNALS.map((s, i) => (
          <Reveal key={s.t} delay={(i % 3) * 0.05}>
            <div className="card p-7 h-full" data-testid={`trust-signal-${i}`}>
              <s.icon className="w-8 h-8 text-[#FFB800] mb-4" strokeWidth={1.75} />
              <h3 className="font-head font-700 text-lg uppercase tracking-tight text-white">{s.t}</h3>
              <p className="font-body text-[#94A3B8] mt-2 leading-relaxed">{s.d}</p>
            </div>
          </Reveal>
        ))}
      </div>

      {hasReviews ? (
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 mt-14">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={t.n} delay={(i % 3) * 0.05}>
              <ReviewCard t={t} i={i} />
            </Reveal>
          ))}
        </div>
      ) : (
        <Reveal>
          <div className="card p-8 md:p-10 mt-8 border-l-4 border-[#FFB800] flex flex-col md:flex-row md:items-center justify-between gap-6" data-testid="reviews-invite">
            <div>
              <p className="font-head font-800 text-2xl uppercase tracking-tight">Running IronLog on your fleet?</p>
              <p className="font-body text-[#94A3B8] mt-2 max-w-xl">We&apos;re a proudly new, Aussie-built platform. Be one of the first crews to share how IronLog keeps your gear running — your words could feature right here.</p>
            </div>
            <a href="#demo" className="btn-signal font-head h-13 px-7 flex items-center gap-2 shrink-0" style={{ height: "3.25rem" }} data-testid="reviews-invite-cta">Share your story <ArrowUpRight className="w-5 h-5" strokeWidth={2.5} /></a>
          </div>
        </Reveal>
      )}
    </section>
  );
}

function MobileApp() {
  return (
    <section id="app" className="relative overflow-hidden border-t border-white/10" data-testid="mobile-app-section">
      <img src={IMG.truck} alt="Freight truck at night" className="absolute inset-0 w-full h-full object-cover opacity-25" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, #0D0E11, rgba(13,14,17,0.6), #0D0E11)" }} />
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 py-28 text-center md:text-left">
        <Reveal>
          <h2 className="font-head font-900 text-5xl md:text-7xl uppercase tracking-tighter leading-[0.9] max-w-3xl">Take IronLog <span className="text-[#FFB800]">to the field.</span></h2>
          <p className="font-body text-lg text-[#94A3B8] max-w-xl mt-6">The web command center pairs with the IronLog mobile app. Same fleet, same data — log services and scan QR tags right from the cab.</p>
          <div className="flex flex-col sm:flex-row gap-4 mt-10 justify-center md:justify-start">
            <a href="#" className="flex items-center gap-3 bg-black border border-white/20 hover:border-white/50 transition-colors px-6 h-16 rounded-md" data-testid="app-store-btn">
              <Apple className="w-8 h-8 text-white" />
              <div className="text-left"><p className="font-body text-[10px] text-[#94A3B8] uppercase">Download on the</p><p className="font-head font-700 text-xl leading-none">App Store</p></div>
            </a>
            <a href="#" className="flex items-center gap-3 bg-black border border-white/20 hover:border-white/50 transition-colors px-6 h-16 rounded-md" data-testid="play-store-btn">
              <Play className="w-7 h-7 text-white fill-white" />
              <div className="text-left"><p className="font-body text-[10px] text-[#94A3B8] uppercase">Get it on</p><p className="font-head font-700 text-xl leading-none">Google Play</p></div>
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function DemoRequest() {
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", message: "", company_url: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error("Please add your name and email."); return; }
    setSending(true);
    try {
      await api.post("/public/demo-request", form);
      setDone(true);
    } catch (err) {
      if (err.response?.status === 429) toast.error("Too many requests — please try again in a little while.");
      else toast.error("Something went wrong — please try again or email us.");
    } finally { setSending(false); }
  };

  return (
    <section id="demo" className="bg-[#0D0E11] border-t border-white/10 py-28" data-testid="demo-section">
      <div className="max-w-[1400px] mx-auto px-6 grid lg:grid-cols-[1fr_1.1fr] gap-14 items-center">
        <div>
          <div className="flex items-center gap-3 mb-6">
            <span className="w-8 h-[2px] bg-[#FFB800]" />
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#FFB800]">Book A Demo</span>
          </div>
          <h2 className="font-head font-900 text-5xl md:text-7xl uppercase tracking-tighter leading-[0.9]">See it on <span className="text-[#FFB800]">your fleet.</span></h2>
          <p className="font-body text-lg text-[#94A3B8] max-w-lg mt-6">Tell us about your machines and we&apos;ll walk you through IronLog — reminders, QR tags, pre-starts and reporting. No pressure, no spreadsheets.</p>
        </div>

        {done ? (
          <div className="card p-10 text-center" data-testid="demo-success">
            <CheckCircle2 className="w-14 h-14 text-[#00E676] mx-auto mb-4" />
            <h3 className="font-head font-800 text-3xl uppercase tracking-tight">Request received</h3>
            <p className="font-body text-[#94A3B8] mt-2">Thanks {form.name.split(" ")[0] || ""} — we&apos;ll be in touch shortly at {form.email}.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="card p-6 md:p-8 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="demo-form">
            <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}>
              <label>Company URL<input type="text" tabIndex={-1} autoComplete="off" value={form.company_url} onChange={(e) => setForm({ ...form, company_url: e.target.value })} data-testid="demo-hp" /></label>
            </div>
            <div className="sm:col-span-2"><label className="il-label">Name *</label><input className="il-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="demo-name" /></div>
            <div><label className="il-label">Email *</label><input className="il-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="demo-email" /></div>
            <div><label className="il-label">Phone</label><input className="il-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="demo-phone" /></div>
            <div className="sm:col-span-2"><label className="il-label">Company / Fleet</label><input className="il-input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="demo-company" /></div>
            <div className="sm:col-span-2"><label className="il-label">Tell us about your fleet</label><textarea className="il-input py-3" style={{ minHeight: "6rem" }} placeholder="How many machines? What do you run?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="demo-message" /></div>
            <div className="sm:col-span-2">
              <button disabled={sending} className="btn-signal w-full h-14 text-lg font-head flex items-center justify-center gap-2 disabled:opacity-60" data-testid="demo-submit">
                <Send className="w-5 h-5" /> {sending ? "Sending…" : "Request My Demo"}
              </button>
              <p className="font-body text-xs text-[#94A3B8] mt-3 text-center">By submitting, you agree we can contact you about IronLog. We never share your details.</p>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-white/10 bg-[#0D0E11]" data-testid="landing-footer">
      <div className="max-w-[1400px] mx-auto px-6 py-16 grid md:grid-cols-[1.5fr_1fr_1fr] gap-10">
        <div>
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-8 h-8 bg-[#FFB800] flex items-center justify-center"><Wrench className="w-5 h-5 text-black" strokeWidth={2.5} /></div>
            <span className="font-head font-900 text-2xl tracking-tight">IRONLOG</span>
          </div>
          <p className="font-body text-[#94A3B8] max-w-xs">Machinery maintenance & fleet management for people who work with iron.</p>
          <div className="mt-5">
            <p className="font-head font-700 uppercase tracking-wide text-xs mb-2 text-[#94A3B8]">Follow</p>
            <a href="https://www.facebook.com/share/19GYcfAfJE/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" aria-label="IronLog on Facebook" className="w-10 h-10 flex items-center justify-center border border-white/15 text-white hover:bg-[#FFB800] hover:text-black hover:border-[#FFB800] transition-colors" data-testid="footer-facebook-link"><Facebook className="w-5 h-5" /></a>
          </div>
        </div>
        <div>
          <p className="font-head font-700 uppercase tracking-wide text-sm mb-4 text-[#94A3B8]">Product</p>
          <ul className="space-y-2 font-body text-white/80">
            <li><a href="#features" className="hover:text-[#FFB800]">Features</a></li>
            <li><a href="#pricing" className="hover:text-[#FFB800]">Pricing</a></li>
            <li><Link to="/register" className="hover:text-[#FFB800]">Start Free</Link></li>
          </ul>
        </div>
        <div>
          <p className="font-head font-700 uppercase tracking-wide text-sm mb-4 text-[#94A3B8]">Fleet Types</p>
          <ul className="space-y-2 font-body text-white/80">
            <li className="flex items-center gap-2"><Tractor className="w-4 h-4 text-[#FFB800]" /> Farmers</li>
            <li className="flex items-center gap-2"><Truck className="w-4 h-4 text-[#FFB800]" /> Contractors</li>
            <li className="flex items-center gap-2"><LineChart className="w-4 h-4 text-[#FFB800]" /> Councils & Golf</li>
          </ul>
          <p className="font-head font-700 uppercase tracking-wide text-sm mb-4 mt-8 text-[#94A3B8]">Legal</p>
          <ul className="space-y-2 font-body text-white/80">
            <li><Link to="/privacy" className="hover:text-[#FFB800]" data-testid="footer-privacy-link">Privacy Policy</Link></li>
            <li><Link to="/terms" className="hover:text-[#FFB800]" data-testid="footer-terms-link">Terms of Service</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 py-6 text-center font-mono text-xs text-[#94A3B8]">© {new Date().getFullYear()} IRONLOG · Built rugged in AUD</div>
    </footer>
  );
}

export default function Landing() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  return (
    <div className="grain relative bg-[#0D0E11] min-h-screen">
      <Nav />
      <Hero />
      <Marquee />
      <Manifesto />
      <Features />
      <FieldStrip />
      <Pricing />
      <Testimonials />
      <MobileApp />
      <DemoRequest />
      <Footer />
    </div>
  );
}
